import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { Section } from '../../../../components/ui/Section';
import { CatalogueGovernanceClient, DataQualityIssueRow } from '../../../../app/services/api/catalogueGovernance';
import ApprovalStatusChip from './ApprovalStatusChip';
import SeverityChip from './SeverityChip';
import RowActionsMenu from './RowActionsMenu';

const ISSUE_TYPE_KEY: Record<DataQualityIssueRow['issueType'], string> = {
  MissingAllergens: 'MissingAllergens',
  MissingCarbon: 'MissingCarbon',
  MissingNutrition: 'MissingNutrition',
  BadUomConversion: 'BadUomConversion',
  MissingPrice: 'MissingPrice',
  InconsistentPackSize: 'InconsistentPackSize',
  UnexpectedPriceMovement: 'UnexpectedPriceMovement',
  InactiveItemUsedInRecipe: 'InactiveItemUsedInRecipe',
};

const DataQualityTab = () => {
  const { t } = useTranslation('catalogueGovernance');
  const navigate = useNavigate();
  const [rows, setRows] = useState<DataQualityIssueRow[]>([]);

  const load = () => CatalogueGovernanceClient.getDataQualityIssues().then(setRows);

  useEffect(() => {
    load();
  }, []);

  const handleAction = async (action: (id: string) => Promise<void>, id: string) => {
    await action(id);
    load();
  };

  const isFinal = (status: DataQualityIssueRow['status']) => status !== 'Open';

  return (
    <Section>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('dataQuality.columnIssueType', 'Issue type')}</TableCell>
            <TableCell>{t('dataQuality.columnSupplier', 'Supplier')}</TableCell>
            <TableCell>{t('dataQuality.columnCatalogue', 'Catalogue')}</TableCell>
            <TableCell>{t('dataQuality.columnProduct', 'Product')}</TableCell>
            <TableCell>{t('dataQuality.columnSeverity', 'Severity')}</TableCell>
            <TableCell>{t('dataQuality.columnDescription', 'Description')}</TableCell>
            <TableCell>{t('dataQuality.columnSuggestedFix', 'Suggested fix')}</TableCell>
            <TableCell>{t('dataQuality.columnDownstreamImpact', 'Downstream impact')}</TableCell>
            <TableCell>{t('dataQuality.columnStatus', 'Status')}</TableCell>
            <TableCell>{t('dataQuality.columnActions', 'Actions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={10}>{t('dataQuality.emptyState', 'No data quality issues to show.')}</TableCell></TableRow>
          )}
          {rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell>{t(`dataQuality.issueType.${ISSUE_TYPE_KEY[row.issueType]}`, row.issueType)}</TableCell>
              <TableCell>{row.supplier}</TableCell>
              <TableCell>{row.catalogue}</TableCell>
              <TableCell>{row.product}</TableCell>
              <TableCell><SeverityChip severity={row.severity} /></TableCell>
              <TableCell>{row.description}</TableCell>
              <TableCell>{row.suggestedFix}</TableCell>
              <TableCell>{row.downstreamImpact}</TableCell>
              <TableCell><ApprovalStatusChip status={row.status} /></TableCell>
              <TableCell>
                <RowActionsMenu actions={[
                  {
                    label: t('dataQuality.actionOpenProduct', 'Open product'),
                    onClick: () => navigate(`/procurement/products?searchTerm=${encodeURIComponent(row.product)}`),
                  },
                  {
                    label: t('dataQuality.actionAcceptSuggestion', 'Accept suggestion'),
                    disabled: isFinal(row.status),
                    onClick: () => handleAction(CatalogueGovernanceClient.acceptDataQualitySuggestion.bind(CatalogueGovernanceClient), row.id),
                  },
                  {
                    label: t('dataQuality.actionIgnoreWarning', 'Ignore warning'),
                    disabled: isFinal(row.status),
                    onClick: () => handleAction(CatalogueGovernanceClient.ignoreDataQualityWarning.bind(CatalogueGovernanceClient), row.id),
                  },
                  {
                    label: t('dataQuality.actionMarkResolved', 'Mark resolved'),
                    disabled: isFinal(row.status),
                    onClick: () => handleAction(CatalogueGovernanceClient.markDataQualityResolved.bind(CatalogueGovernanceClient), row.id),
                  },
                ]} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Section>
  );
};

export default DataQualityTab;
